import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import React, { ReactNode } from "react";
import DateOnlyFormat from "components/DateOnlyFormat";
import { FormatDateFormat, toUTCDate } from "src/utils/date";
import { match, P } from "ts-pattern";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { Currency } from "src/utils/localization/currency";
import {
  AggregationType,
  TokenValueEncoded,
  TokenValuePercentage
} from "@silversea-ssc/dotcom-read-model-types/Tokens";
import { isPresent } from "src/utils/checks";
import { LanguageCode } from "src/utils/localization/languages/types";

const numberFormatOptions = (currency: string): Intl.NumberFormatOptions => ({
  style: "currency",
  currency: currency,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

type AmountPrefixProps = {
  aggregationType: AggregationType;
  aggregatedValuesIdentical?: boolean;
};

const AmountPrefix: React.FC<React.PropsWithChildren<AmountPrefixProps>> = ({
  aggregationType,
  aggregatedValuesIdentical
}) => {
  const { t } = useLingui();
  const prefix = formatAmountPrefix(aggregationType, aggregatedValuesIdentical);
  return prefix ? <React.Fragment>{t(prefix)} </React.Fragment> : null;
};

const formatAmountPrefix = (
  aggregationType: AggregationType,
  aggregatedValuesIdentical?: boolean
) => {
  return match([aggregationType, aggregatedValuesIdentical])
    .with(["min", true], () => msg({ id: `token-value-at` }))
    .with(["min", P.union(false, P.nullish)], () =>
      msg({ id: `token-value-from` })
    )
    .with([P.any, true], () => null)
    .with(["max", P.any], () => msg({ id: `token-value-up-to` }))
    .otherwise(() => null);

  // if (aggregationType === "min")
  //   return i18n._({
  //     id: aggregatedValuesIdentical ? t`token-value-at` : t`token-value-from`
  //   });
  // if (aggregatedValuesIdentical) return null;
  // if (aggregationType === "max") return i18n._({ id: t`token-value-up-to` });
  // return null;
};

const dateFormatOptions: FormatDateFormat = {
  dateStyle: "long"
};

function formattedValue(
  i18n: ReturnType<typeof useLingui>["i18n"],
  _placeholder: string,
  value: TokenValueEncoded,
  currentCurrency: Currency | undefined
): JSX.Element {
  return match(value)
    .with({ type: "date" }, (v) => (
      <DateOnlyFormat value={toUTCDate(v.date)} format={dateFormatOptions} />
    ))
    .with({ type: "percentage" }, (v) => (
      <React.Fragment>{formatPercentage(v)}</React.Fragment>
    ))
    .with({ type: "amount" }, (v) => {
      let formattedValue: string;
      try {
        formattedValue = i18n.number(v.amount, numberFormatOptions(v.currency));
      } catch {
        // Intl.NumberFormat missing, or currency not a valid ISO 4217 code
        formattedValue = `${v.currency} ${v.amount}`;
      }

      return (
        <React.Fragment>
          <AmountPrefix
            aggregationType={v.aggregationType}
            aggregatedValuesIdentical={v.aggregatedValuesIdentical ?? undefined}
          />
          {fixNonUSCountryUSDCurrencySymbol(
            formattedValue,
            i18n.locale as LanguageCode,
            v.currency,
            currentCurrency
          )}
        </React.Fragment>
      );
    })
    .with({ type: "string" }, (v) => <React.Fragment>{v.value}</React.Fragment>)
    .otherwise(() => <React.Fragment></React.Fragment>);
}

const formatPercentage = (token: TokenValuePercentage): string =>
  `${token.percentage}%`;

interface PlaceholderValuesFormatterProps {
  placeholder: string;
  value: TokenValueEncoded;
  // Show ••• instead of a blank while the token values are still being loaded.
  loadingPlaceholder?: boolean;
}

const PlaceholderValuesFormatter: React.FC<
  React.PropsWithChildren<PlaceholderValuesFormatterProps>
> = ({ placeholder, value, loadingPlaceholder = false }) => {
  const geolocation = useGeolocation();
  const { i18n } = useLingui();

  // The <span> wrapper is not cosmetic. Tokens usually live inside a rich text
  // container mixing text and placeholders (<p>Save up to <token/> on selected
  // voyages</p>): without a wrapper, the number of nodes rendered here changes
  // between loading and loaded, so React has to insert/remove nodes *next to*
  // the surrounding text. `translate="no"` keeps browser translators out, as
  // they re-parent that text into <font> elements and make insertBefore /
  // removeChild fail with NotFoundError.
  if (!value && loadingPlaceholder) return <span translate="no">•••</span>;
  // Preview builds show the missing token's name to the content editors.
  if (!value && isPresent(process.env.SANITY_API_TOKEN))
    return <span translate="no">{placeholder}</span>;
  if (!value) return <span translate="no"> </span>;

  return (
    <span translate="no">
      {formattedValue(i18n, placeholder, value, geolocation.defaultCurrency)}
    </span>
  );
};

export default PlaceholderValuesFormatter;

export function renderPlaceholderValue(
  placeholder: string,
  value: TokenValueEncoded,
  loadingPlaceholder = false
): ReactNode {
  return (
    <PlaceholderValuesFormatter
      key={`${placeholder}`}
      placeholder={placeholder}
      value={value}
      loadingPlaceholder={loadingPlaceholder}
    />
  );
}

const fixNonUSCountryUSDCurrencySymbol = (
  formattedValue: string,
  languageCode: LanguageCode | undefined,
  amountCurrency: Currency | undefined,
  currentCurrency: Currency | undefined
): string => {
  const extendedUSDCurrencySymbol =
    amountCurrency == "USD" && currentCurrency != "USD";

  if (!extendedUSDCurrencySymbol) {
    return formattedValue; // renders in US or the currency is not USD
  }
  if (languageCode === "de" || languageCode == "en") {
    return formattedValue.replace("$", "US$");
  }

  return formattedValue;
};
